<template>
    <section :class="`flyout-wrapped-content${this.show ? ' --open' : ''}`">
        <component :ref="barDef"
                   :is="barComponent"
                   :class="`flyout-wrapped-content__bar ${ modifier }`"
                   :flyout-open="show"
                   @click="$emit('toggle')" />
        <div :ref="def"
             :class="`flyout-wrapped-content__content${this.show ? ' --open' : ''} ${ conditionalModifier }`"
             :tabindex="tabIndexForWrapper"
             @keyup.esc="onFlyoutEscape">
            <slot />
        </div>
    </section>
</template>

<script>

import LoopedTabbingControl from '../looped-tabbing-control'

export default {

    mixins: [
        LoopedTabbingControl,
    ],

    props: {
        /**
         * @returns {Vue|VueProp}
         */
        barComponent: {
            type: Object,
            required: true,
        },

        /**
         * @returns {boolean|VueProp}
         */
        show: {
            type: Boolean,
            default: false,
        },

        /**
         * @returns {string|VueProp}
         */
        modifier: {
            type: String,
            default: '',
        },

        /**
         * @returns {string|VueProp}
         */
        def: {
            type: String,
            default: 'wrappedContent',
        },

        /**
         * @returns {string|VueProp}
         */
        barDef: {
            type: String,
            default: 'controlBar',
        },
    },

    watch: {

        /**
         * @returns {boolean}
         */
        show() {
            this.tabbableRootNodeRef = this.$refs[this.def]
            this._setupKeyboardNavigation()
        },
    },

    computed: {

        /**
         * Adapts the provided modifier for adjusting when SAP Metrics are shown.
         * @returns {string}
         */
        conditionalModifier() {
            return this.modifier === '--metrics'
                ? this.$scheme.showSapMetrics ? '--metrics --metrics-3' : '--metrics --metrics-2'
                : this.modifier
        },

        /**
         * @returns {number}
         */
        tabIndexForWrapper() {
            return this.isMidViewportOrLess ? 0 : -1
        },
    },

    mounted() {
        this.tabbableRootNodeRef = this.$refs[this.def]
        this._setupKeyboardNavigation()
    },

    methods: {

        /**
         * Sets up the component for keyboard navigation.
         */
        _setupKeyboardNavigation() {
            this.$nextTick(() => {
                this.$logger.logObject({ ref: this.tabbableRootNodeRef, isMidOrLess: this.isMidViewportOrLess }, 'Setting up Keyboard Navigation', 'Tabbing Flyout')
                if (this.tabbableRootNodeRef) {
                    if (this.isMidViewportOrLess)
                        this.bindTabbingEvents()
                    else
                        this.clearTabbingEvents()
                }
            })
        },

        /**
         * Called when the escape key is hit while the flyout is shown.
         */
        onFlyoutEscape() {
            // noinspection JSUnresolvedReference
            this.$refs[this.barDef].$el.focus()
            this.$emit('toggle')
        },
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\plan-builder\_flyout-wrapped-content.scss

.flyout-wrapped-content {
    position: relative;

    &.--open {
        z-index: z-index('dd');
    }

    @include element('bar') {
        height: rem(38px);
        width: 97%;

        &.--metrics {
            margin-left: 3%;

            @include bp-min-small {
                margin-left: 0;
                width: 100%;
            }
        }

        &.--filters {
            margin-right: 3%;

            @include bp-min-small {
                margin-right: 0;
                width: rem(175px);
            }
        }

        @include bp-min-mid {
            display: none;
        }
    }

    @include element('content') {
        display: none;
        position: fixed;
        bottom: calc(#{$footer-height-base} + #{$content-flyout-padding} + #{$mobile-double-button-bar-allowance});
        left: $content-flyout-padding;
        right: $content-flyout-padding;
        padding: rem(5px) rem(10px);
        font-size: 0.8rem;
        line-height: 1rem;
        overflow-y: auto;

        @include bp-min-small {
            bottom: calc(#{$footer-height-base} + #{$content-flyout-padding} + #{$mobile-button-bar-allowance});
        }

        @include bp-min-mid {
            display: block;
            position: static;
            overflow-y: hidden;
            font-size: 0.9rem;
            padding: 0;
        }

        &.--open {
            display: block;
        }

        &.--filters {
            max-height: rem(440px);

            @include bp-min-small {
                max-width: rem(440px);
            }

            @include bp-min-mid {
                max-width: none;
            }
        }

        &.--metrics {

            @include bp-min-small {
                max-width: rem(300px);
                left: auto;
            }

            @include bp-min-mid {
                max-width: none;
            }
        }

        &.--metrics-2 {
            max-height: rem(146px);

            @include bp-min-mid {
                max-height: none;
            }
        }

        &.--metrics-3 {
            max-height: rem(210px);

            @include bp-min-mid {
                max-height: none;
            }
        }
    }
}

</style>
